import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { HeadingEditorProps } from "./HeadingEditor.types";
import { HeadingBlockContent } from "@/modules/hub/features/ResourceManagement/components/SortableBlock/types";

const headingLevels = [
  { value: "h2", label: "Heading 2", className: "text-2xl font-bold" },
  { value: "h3", label: "Heading 3", className: "text-xl font-bold" },
  { value: "h4", label: "Heading 4", className: "text-lg font-semibold" },
  { value: "h5", label: "Heading 5", className: "text-base font-semibold" },
  { value: "h6", label: "Heading 6", className: "text-sm font-semibold" },
] as const;

export function HeadingEditor({ value, onChange }: HeadingEditorProps) {
  const [text, setText] = useState("");
  const [level, setLevel] = useState<HeadingBlockContent["level"]>("h2");

  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value) as HeadingBlockContent;
        setText(parsed.text || "");
        setLevel(parsed.level || "h2");
      } catch (e) {
        console.error("Failed to parse heading content:", e);
        setText("");
        setLevel("h2");
      }
    }
  }, [value]);

  const updateContent = (
    newText: string,
    newLevel: HeadingBlockContent["level"],
  ) => {
    const content: HeadingBlockContent = {
      text: newText,
      level: newLevel,
    };
    onChange(JSON.stringify(content));
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setText(newText);
    updateContent(newText, level);
  };

  const handleLevelChange = (newLevel: string) => {
    const typedLevel = newLevel as HeadingBlockContent["level"];
    setLevel(typedLevel);
    updateContent(text, typedLevel);
  };

  const currentHeading = headingLevels.find((h) => h.value === level);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="heading-text">Heading Text</Label>
        <Input
          id="heading-text"
          onChange={handleTextChange}
          placeholder="Enter heading text..."
          type="text"
          value={text}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="heading-level">Heading Level</Label>
        <Select onValueChange={handleLevelChange} value={level}>
          <SelectTrigger id="heading-level">
            <SelectValue placeholder="Select heading level" />
          </SelectTrigger>
          <SelectContent>
            {headingLevels.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          H1 is reserved for page titles. Choose H2-H6 for content headings.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Preview</Label>
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div
              className={`${currentHeading?.className} text-gray-900 dark:text-white`}
            >
              {text || "Your heading will appear here..."}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
